import React, { useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Badge,
    Button,
    Pagination,
    PaginationItem,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input
} from "reactstrap";
import Swal from "sweetalert2";
import apiClient from "api/ApiClient.js";
import { getReputationFlags } from "../../api/ApiServices";
import { styles, renderPaginationItems } from "./ReputationUtils";

const AntiGamingAuditsTab = ({ selectedUser }) => {
    const [dynamicFlags, setDynamicFlags] = useState([]);
    const [flagPage, setFlagPage] = useState(1);
    const [flagTotal, setFlagTotal] = useState(0);
    const [flagPages, setFlagPages] = useState(1);
    const [flagLoading, setFlagLoading] = useState(false);
    const [isFlagFilterModalOpen, setIsFlagFilterModalOpen] = useState(false);
    const [flagFilters, setFlagFilters] = useState({
        severity: "",
        status: "",
        dateRange: ""
    });

    const [antiGamingFlags, setAntiGamingFlags] = useState([]);

    const [dynamicBadges, setDynamicBadges] = useState([]);
    const [badgePage, setBadgePage] = useState(1);
    const [badgeTotal, setBadgeTotal] = useState(0);
    const [badgePages, setBadgePages] = useState(1);
    const [badgeLoading, setBadgeLoading] = useState(false);

    const toggleFlagFilterModal = () => setIsFlagFilterModalOpen(!isFlagFilterModalOpen);

    const fetchFlagList = async (page = 1, filterParams = flagFilters) => {
        setFlagLoading(true);
        try {
            const params = {
                page,
                limit: 5,
                ...filterParams
            };
            const response = await getReputationFlags(params);
            if (response.status === true && response.payload?.flags) {
                setDynamicFlags(response.payload.flags);
                setFlagTotal(response.payload.total || 0);
                setFlagPages(response.payload.pages || 1);
                setFlagPage(response.payload.page || 1);
            } else {
                setDynamicFlags([]);
                setFlagTotal(0);
                setFlagPages(1);
            }
        } catch (err) {
            console.error("Error fetching flag list:", err);
            setDynamicFlags([]);
        } finally {
            setFlagLoading(false);
        }
    };

    const applyFlagFilters = () => {
        fetchFlagList(1, flagFilters);
        setIsFlagFilterModalOpen(false);
    };

    const clearFlagFilters = () => {
        setFlagFilters({
            severity: "",
            status: "",
            dateRange: ""
        });
        fetchFlagList(1, {});
        setIsFlagFilterModalOpen(false);
    };

    const fetchDynamicBadges = async (page = 1) => {
        setBadgeLoading(true);
        try {
            const response = await apiClient.get(`/private/reputation/badges?page=${page}&limit=5`);
            if (response.data.status && response.data.payload?.badges) {
                setDynamicBadges(response.data.payload.badges);
                setBadgeTotal(response.data.payload.total || 0);
                setBadgePages(response.data.payload.totalPages || 1);
                setBadgePage(response.data.payload.page || 1);
            } else {
                setDynamicBadges([]);
                setBadgeTotal(0);
                setBadgePages(1);
            }
        } catch (err) {
            console.error("Error fetching dynamic badges:", err);
            setDynamicBadges([]);
        } finally {
            setBadgeLoading(false);
        }
    };

    useEffect(() => {
        fetchFlagList(1);
        fetchDynamicBadges(badgePage);
    }, []);

    useEffect(() => {
        fetchDynamicBadges(badgePage);
    }, [badgePage]);

    useEffect(() => {
        if (dynamicFlags && dynamicFlags.length > 0) {
            const uniqueFlags = Array.from(new Map(dynamicFlags.map(f => [f.id, f])).values());
            const sortedDynamic = uniqueFlags.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            const formattedDynamic = sortedDynamic.map(f => ({
                ...f,
                timestamp: f.timestamp ? new Date(f.timestamp).toLocaleString() : new Date().toLocaleString(),
                isDynamic: true
            }));
            setAntiGamingFlags(formattedDynamic);
        } else {
            setAntiGamingFlags([]);
        }
    }, [dynamicFlags]);

    const handleAntiGamingAction = (action, flagId) => {
        if (action === "Ignore") {
            setAntiGamingFlags(antiGamingFlags.filter(f => f.id !== flagId));
            Swal.fire({
                title: "Flag Ignored",
                text: `Flag ${flagId} marked as False Positive.`,
                icon: "success",
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false,
                toast: true,
                position: 'top-end'
            });
            return;
        }

        Swal.fire({
            title: `${action} Flag`,
            text: `Are you sure you want to ${action.toLowerCase()} this algorithmic flag?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: action === "Punish" ? "#f5365c" : "#5e72e4",
        }).then((result) => {
            if (result.isConfirmed) {
                setAntiGamingFlags(antiGamingFlags.filter(f => f.id !== flagId));
                Swal.fire({
                    title: "Resolved!",
                    text: `Flag ${flagId} has been resolved successfully.`,
                    icon: "success",
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end'
                });
            }
        });
    };

    return (
        <>
            {/* Header row to allow filtering */}
            <div className="d-flex justify-content-end mb-3">
                <Button
                    color="primary"
                    size="sm"
                    outline
                    style={{ borderRadius: "8px" }}
                    onClick={toggleFlagFilterModal}
                >
                    <i className="fas fa-filter mr-2" /> Filter Flags
                    {Object.values(flagFilters).some(v => v) && (
                        <Badge color="danger" pill className="ml-2" style={{ fontSize: "10px" }}>
                            Active
                        </Badge>
                    )}
                </Button>
            </div>

            <Row>
                <Col lg="6">
                    <Card className="shadow" style={{ borderRadius: "16px", border: "none" }}>
                        <CardHeader className="border-0 bg-white d-flex justify-content-between align-items-center" style={{ borderRadius: "16px 16px 0 0", padding: "24px" }}>
                            <h3 className="mb-0 text-dark font-weight-700">Anti-Gaming Monitor</h3>
                            <Badge color="danger" pill>Real-time Flags</Badge>
                        </CardHeader>
                        <CardBody className="bg-secondary" style={{ borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" }}>
                            {antiGamingFlags && antiGamingFlags.length > 0 ? antiGamingFlags.map(flag => (
                                <div key={flag.id} className="bg-white p-4 mb-3 rounded shadow-sm" style={{ borderLeft: `4px solid ${flag.severity === 'Critical' ? '#f5365c' : '#fb6340'}` }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="font-weight-bold text-dark"><i className="fas fa-biohazard text-danger mr-2" /> {flag.type || "Flag"}</span>
                                        <small className="text-muted"><i className="far fa-clock mr-1" />{flag.timestamp || new Date().toLocaleString()}</small>
                                    </div>
                                    <p className="text-sm text-muted mb-3">User <span className="font-weight-bold text-dark">{flag.user || "Unknown"}</span>: {flag.details || "No details provided"}</p>
                                    {/* <div className="d-flex justify-content-end gap-2">
                                        <Button size="sm" color="secondary" onClick={() => handleAntiGamingAction("Ignore", flag.id)}>Ignore</Button>
                                        <Button size="sm" color={selectedUser?.security?.isUnderReview ? "danger" : "warning"} onClick={() => handleAntiGamingAction("Warn", flag.id)}>Warn User</Button>
                                        <Button size="sm" color="danger" onClick={() => handleAntiGamingAction("Punish", flag.id)}>Apply Penalty</Button>
                                    </div> */}
                                </div>
                            )) : (
                                <div className="text-center py-5">
                                    <i className="fas fa-check-circle fa-3x text-success mb-3" />
                                    <h4 className="text-muted">No Gaming Activities Detected</h4>
                                </div>
                            )}

                            {flagPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3 p-3 bg-white rounded shadow-sm">
                                    <span className="text-muted small">
                                        Showing Page {flagPage} of {flagPages} ({flagTotal} flags)
                                    </span>
                                    <nav aria-label="...">
                                        <Pagination
                                            className="pagination justify-content-end mb-0"
                                            listClassName="justify-content-end mb-0"
                                            size="sm"
                                        >
                                            <PaginationItem disabled={flagPage === 1 || flagLoading}>
                                                <PaginationLink
                                                    href="#pablo"
                                                    onClick={(e) => { e.preventDefault(); fetchFlagList(flagPage - 1); }}
                                                    tabIndex="-1"
                                                >
                                                    <i className="fas fa-angle-left" />
                                                </PaginationLink>
                                            </PaginationItem>

                                            {renderPaginationItems(flagPage, flagTotal, 5, (page) => fetchFlagList(page))}

                                            <PaginationItem disabled={flagPage === flagPages || flagLoading}>
                                                <PaginationLink
                                                    href="#pablo"
                                                    onClick={(e) => { e.preventDefault(); fetchFlagList(flagPage + 1); }}
                                                >
                                                    <i className="fas fa-angle-right" />
                                                </PaginationLink>
                                            </PaginationItem>
                                        </Pagination>
                                    </nav>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                <Col lg="6">
                    <Card className="shadow" style={{ borderRadius: "16px", border: "none" }}>
                        <CardHeader className="border-0 bg-white" style={{ borderRadius: "16px 16px 0 0", padding: "24px" }}>
                            <h3 className="mb-0 text-dark font-weight-700">Badge Audit History</h3>
                        </CardHeader>
                        <CardBody className="bg-secondary" style={{ borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" }}>
                            <div>
                                <h6 className="text-uppercase text-muted ls-1 mb-3">Dynamic Badge Audit</h6>
                                {badgeLoading ? (
                                    <div className="text-center py-4">
                                        <i className="fas fa-spinner fa-spin fa-2x text-primary" />
                                    </div>
                                ) : dynamicBadges.length > 0 ? (
                                    <>
                                        {dynamicBadges.map(audit => (
                                            <div key={audit.id} style={styles.auditCard}>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <Badge color="success" pill>Grant</Badge>
                                                    <small className="text-muted">
                                                        {audit.createdAt ? new Date(audit.createdAt).toLocaleString(undefined, {
                                                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                        }) : new Date().toLocaleString()}
                                                    </small>
                                                </div>
                                                <div className="mt-2 text-dark">
                                                    <span className="font-weight-bold">{audit.badgeName || "Unknown Badge"}</span> granted to
                                                    <span className="font-weight-bold ml-1">{audit.user?.name || "Unknown User"}</span>
                                                    <div className="text-xs text-muted mt-1">Email: {audit.user?.email || "No email"}</div>
                                                </div>
                                            </div>
                                        ))}

                                        <div className="d-flex justify-content-between align-items-center mt-3 p-3 bg-white rounded shadow-sm">
                                            <span className="text-muted small">
                                                Page {badgePage} of {badgePages}
                                            </span>
                                            <nav aria-label="...">
                                                <Pagination
                                                    className="pagination justify-content-end mb-0"
                                                    listClassName="justify-content-end mb-0"
                                                    size="sm"
                                                >
                                                    <PaginationItem disabled={badgePage === 1 || badgeLoading}>
                                                        <PaginationLink
                                                            href="#pablo"
                                                            onClick={(e) => { e.preventDefault(); setBadgePage(badgePage - 1); }}
                                                            tabIndex="-1"
                                                        >
                                                            <i className="fas fa-angle-left" />
                                                        </PaginationLink>
                                                    </PaginationItem>

                                                    {renderPaginationItems(badgePage, badgeTotal, 5, setBadgePage)}

                                                    <PaginationItem disabled={badgePage === badgePages || badgeLoading}>
                                                        <PaginationLink
                                                            href="#pablo"
                                                            onClick={(e) => { e.preventDefault(); setBadgePage(badgePage + 1); }}
                                                        >
                                                            <i className="fas fa-angle-right" />
                                                        </PaginationLink>
                                                    </PaginationItem>
                                                </Pagination>
                                            </nav>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-4 text-muted">
                                        <i className="fas fa-info-circle mr-2" />
                                        No dynamic badge records found.
                                    </div>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Flag Filter Modal */}
            <Modal isOpen={isFlagFilterModalOpen} toggle={toggleFlagFilterModal} centered size="md">
                <ModalHeader toggle={toggleFlagFilterModal} className="bg-secondary border-0">
                    <div className="font-weight-bold">
                        <i className="fas fa-filter mr-2" /> Filter Reputation Flags
                    </div>
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label className="font-weight-bold">Severity Level</Label>
                            <Input
                                type="select"
                                value={flagFilters.severity}
                                onChange={(e) => setFlagFilters({ ...flagFilters, severity: e.target.value })}
                            >
                                <option value="">All Severities</option>
                                <option value="Critical">Critical</option>
                                <option value="Warning">Warning</option>
                                <option value="Info">Info</option>
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label className="font-weight-bold">Flag Status</Label>
                            <Input
                                type="select"
                                value={flagFilters.status}
                                onChange={(e) => setFlagFilters({ ...flagFilters, status: e.target.value })}
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending Review</option>
                                <option value="resolved">Resolved</option>
                                <option value="ignored">Ignored</option>
                            </Input>
                        </FormGroup>

                        <FormGroup>
                            <Label className="font-weight-bold">Date Range</Label>
                            <Input
                                type="select"
                                value={flagFilters.dateRange}
                                onChange={(e) => setFlagFilters({ ...flagFilters, dateRange: e.target.value })}
                            >
                                <option value="">All Time</option>
                                <option value="today">Today</option>
                                <option value="week">Last 7 Days</option>
                                <option value="month">Last 30 Days</option>
                            </Input>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter className="border-0">
                    <Button color="link" onClick={clearFlagFilters}>
                        Clear All Filters
                    </Button>
                    <Button color="primary" onClick={applyFlagFilters}>
                        Apply Filters
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default AntiGamingAuditsTab;
